<template>
	<SuiModal
		class="project-phases-modal"
		is-modal-open
		:is-scrollable="false"
		close-on-mis-click
		@close="onClose"
	>
		<template #header>
			<h2 class="project-phases-modal__header-title">
				{{ $t('modify_phases') }}
			</h2>
		</template>

		<div class="project-phases-modal__content">
			<ProjectModifyPhasesTabSkeleton v-if="initQueryLoading" />
			<ProjectModifyModalPhasesTab
				v-else
				:project-id="projectId"
				:project-phases="projectPhases"
				:has-back-to-back-phases="hasBackToBackPhases"
				:project-start-date="projectStartDate"
				:number-of-tasks="numberOfTasks"
				@phases-updated="onPhasesUpdated"
				@back-to-back-updated="onBackToBackUpdated"
			/>
		</div>

		<template #footer>
			<div class="project-phases-modal__footer-actions">
				<SuiButton
					variant="secondary-outlined"
					:is-disabled="isSavingInProgress"
					@click="onClose"
				>
					{{ $t('cancel') }}
				</SuiButton>

				<SuiButton
					variant="primary"
					:is-disabled="initQueryLoading || isSavingInProgress"
					:is-loading="isSavingInProgress"
					@click="onSave"
				>
					{{ $t('save') }}
				</SuiButton>
			</div>
		</template>
	</SuiModal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { SuiModal, SuiButton, SuiMessageTypeType } from '@developers/scoro-ui';
import { useProjectPhasesModal } from './useProjectPhasesModal';
import { useMessage } from '@/features/Message';
import { useTranslation } from '@/composables';
import ProjectModifyModalPhasesTab from '../ProjectModifyModal/ProjectModifyModalPhasesTab.vue';
import ProjectModifyPhasesTabSkeleton from '../ProjectModifyModal/ProjectModifyPhasesTabSkeleton.vue';
import { type PhaseAndMilestoneListItemType } from '@/features/ProjectPhase';
import { type Project } from '@/generated/graphql';

export interface ProjectPhasesModalProps {
	projectId: Project['id'];
}

const props = defineProps<ProjectPhasesModalProps>();

const emit = defineEmits<{
	(e: 'close'): void;
	(e: 'saved'): void;
}>();

const { t } = useTranslation();
const { addMessage, removeMessage } = useMessage();

const {
	projectPhases,
	hasBackToBackPhases,
	projectStartDate,
	numberOfTasks,
	initQueryLoading,
	isSavingInProgress,
	savePhases,
} = useProjectPhasesModal(props.projectId);

const hasChanged = ref(false);

const onPhasesUpdated = (list: PhaseAndMilestoneListItemType[]) => {
	projectPhases.value = list;
	hasChanged.value = true;
};

const onBackToBackUpdated = (hasBackToBackPhasesValue: boolean) => {
	hasBackToBackPhases.value = hasBackToBackPhasesValue;
	hasChanged.value = true;
};

const closeModal = () => {
	emit('close');
};

const showCloseConfirmationMessage = () => {
	const messageId = 'project-phases-modal-close-confirm-message-id';

	addMessage({
		id: messageId,
		title: t('project_modify_modal_close_confirmation_title'),
		message: t('project_modify_modal_close_confirmation_message'),
		type: SuiMessageTypeType.Warning,
		buttons: [
			{
				isPrimary: true,
				label: t('yes'),
				callback: () => {
					closeModal();
					removeMessage(messageId);
				},
			},
			{
				label: t('no'),
				callback: () => removeMessage(messageId),
			},
		],
	});
};

const onClose = () => {
	if (hasChanged.value) {
		showCloseConfirmationMessage();
		return;
	}

	closeModal();
};

const onSave = async () => {
	const success = await savePhases();
	if (success) {
		emit('saved');
		closeModal();
	}
};
</script>

<style lang="scss" scoped>
.project-phases-modal {
	--sui-modal-justify-content-footer: center;
	--sui-modal-max-width: 98rem;
	--sui-modal-min-width: 98rem;
	--sui-modal-inner-spacing-top: 0;
	--sui-modal-inner-spacing-right: 0;
	--sui-modal-inner-spacing-bottom: 0;
	--sui-modal-inner-spacing-left: 0;
	--sui-modal-max-height: 80vh;

	&__header-title {
		margin-right: 1rem;
	}

	&__content {
		padding: 2rem;
		max-height: 60vh;
		overflow-y: auto;
	}

	&__footer-actions {
		display: flex;
		gap: 1rem;
	}
}
</style>
